import { InitialTabType, TabsType, TabType, TopResultsType } from '@react/SearchResults/SearchTypes'
import { getRequest } from '@site/js/utils/request'
import { useEffect, useState } from 'react'

export const useSearchResults = (resultsFoundLabel = '') => {
  const [isLoadedResults, setIsLoadedResults] = useState(false)
  const [searchParam, setSearchParam] = useState(window.location.search)
  const [tabs, setTabs] = useState<TabsType | null>(null)
  const [numOfResults, setNumOfResults] = useState(0)
  const buildUrlForSearchCriteria = (param: string) => {
    const searchParams = new URLSearchParams(window.location.search)
    searchParams.set('q', param)
    return window.location.origin + window.location.pathname + '?' + searchParams.toString()
  }

  const getParam = (param: string) => new URLSearchParams(window.location.search).get(param)

  const buildUrl = (isLoadMore: boolean, resultsLink: string, id?: string) => {
    const baseUrl = window.location.origin
    const queryParam = `q=${encodeURI(getParam('q'))}`
    const currentPage = isLoadMore ? tabs[id].pageNumber + 1 : 1
    return `${baseUrl}${resultsLink}?${queryParam}&page=${currentPage}`
  }

  const updateTotalResults = () => {
    const resultsCount = document.querySelector('.saas-search-results__number-of-results')
    if (resultsCount) {
      resultsCount.innerHTML = `${numOfResults}  ${resultsFoundLabel}`
    }
  }

  const transformTopResults = (topResults: TopResultsType): TopResultsType => ({
    ...topResults,
    items: topResults?.items?.map(item => ({ ...item, searchCriteria: buildUrlForSearchCriteria(item.searchCriteria) })),
  })

  async function getCsrfToken() {
    const response = await fetch('/libs/granite/csrf/token.json')
    const json = await response.json()
    return json.token
  }

  function increaseSkip(id, isLoadMore) {
    if (isLoadMore) {
      return tabs[id].pageNumber * 10
    } else {
      return 0
    }
  }

  const doSearch = async (tabData: InitialTabType | TabType, isLoadMore = false) => {
    const id = tabData?.tabId
    const searchParam = new URLSearchParams(window.location.search).get('q')
    if (searchParam) {
      try {
        let data
        if (tabData?.title === 'Publisher') {
          const searchParams = new URLSearchParams()
          searchParams.append('search', searchParam) // Your search parameter
          searchParams.append('skip', increaseSkip(id, isLoadMore).toString()) //Page Number

          await fetch('/bin/pg/searchhepublisher', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/x-www-form-urlencoded',
              'CSRF-Token': await getCsrfToken(),
            },
            body: searchParams.toString(),
          })
            .then(response => response.json())
            .then(searchresponse => {
              let showLoadMore = true
              if (searchresponse.value.searchResult.length < 10) {
                showLoadMore = false
              }
              data = {
                title: 'Publisher',
                suggestion: '',
                results: searchresponse.value.searchResult,
                resultsTotal: searchresponse.value.totalCount,
                showLoadMoreButton: showLoadMore,
                tabId: id,
                showLoadMore: showLoadMore,
                ':type': 'pg/components/search/search-tab',
              }
            })
            .catch(error => {
              console.error('Search Response Error:', error)
            })
        } else {
          const response = await getRequest(buildUrl(isLoadMore, tabData.ajaxComponentPath, id))
          data = await response.json()
        }
        const { results, showLoadMore, resultsTotal, suggestion } = data

        if (id.indexOf('-0') !== -1) {
          setNumOfResults(resultsTotal || 0)
        }
        if (isLoadMore) {
          setTabs(prevTabs => ({
            ...prevTabs,
            [id]: {
              tabId: id,
              showLoadMore,
              resultsTotal,
              ajaxComponentPath: tabData.ajaxComponentPath,
              title: tabData.title,
              quickLinks: tabData.quickLinks,
              teasers: tabData.teasers,
              topResults: transformTopResults(tabData.topResults),
              isVisible: true,
              pageNumber: prevTabs[id].pageNumber + 1,
              suggestion,
              results: [...prevTabs[id].results, ...results],
            },
          }))
        } else {
          setTabs(prevTabs => ({
            ...prevTabs,
            [id]: {
              tabId: id,
              showLoadMore,
              resultsTotal,
              results,
              ajaxComponentPath: tabData.ajaxComponentPath,
              title: tabData.title,
              quickLinks: tabData.quickLinks,
              teasers: tabData.teasers,
              topResults: transformTopResults(tabData.topResults),
              pageNumber: 1,
              isVisible: true,
              suggestion,
            },
          }))
        }
      } catch (error) {
        console.error(error)
      }
    } else {
      document.querySelector('.saas-search-results-cmp-header__tabs')?.classList.add('saas-search-tab--is-hidden')
    }
    setIsLoadedResults(true)
  }

  const verifyClick = e => {
    if (e.target.classList.contains('cmp-saas__button')) {
      const searchInput: HTMLInputElement = document.querySelector('.cmp-saas__search-input')
      if (searchInput?.value !== '') {
        setParam(searchInput.value)
        setSearchParam(window.location.search)
      }
    }
    if (e.target.classList.contains('cmp-saas__suggestion-element') || e.target.classList.contains('saas-sugestion__item-link')) {
      setParam(e.target.innerText)
    }
  }
  const handleClearButtonClick = () => {
    setParam('')
    setTabs(null)
    setNumOfResults(0)
    updateTotalResults()
  }
  const initTabs = (tabs: InitialTabType[]) => {
    tabs?.forEach(tab => {
      doSearch(tab)
    })
  }
  const loadMoreClick = (id: string) => {
    const activeTab = tabs[id]
    if (activeTab) {
      doSearch(activeTab, true)
    }
  }
  const setParam = (param: string) => {
    if ('URLSearchParams' in window) {
      setIsLoadedResults(false)
      const searchParams = new URLSearchParams(window.location.search)
      searchParams.set('q', param)
      const newRelativePathQuery = window.location.pathname + '?' + searchParams.toString()
      history.pushState(null, '', newRelativePathQuery)
      const tabsArray = tabs ? Object.values(tabs) : null
      initTabs(tabsArray)
    }
  }

  useEffect(() => {
    const searchClearButton = document.querySelector('.cmp-saas__search-clear-button')
    searchClearButton?.addEventListener('click', handleClearButtonClick)
    updateTotalResults()
    window.removeEventListener('click', verifyClick)
    window.addEventListener('click', verifyClick)
    return () => {
      searchClearButton?.removeEventListener('click', handleClearButtonClick)
      window.removeEventListener('click', verifyClick)
    }
  }, [tabs])

  return {
    loadMoreClick,
    isLoadedResults,
    initTabs,
    tabsData: tabs,
    searchParam,
    setSearchParam,
  }
}
